import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip, Button, CircularProgress } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import FolderIcon from '@mui/icons-material/Folder';

/**
 * Intelligent Contact Section V2 - Handles any contact data format
 * Never shows "Client Representative" - uses smart fallback logic
 */
const ClientContactSectionV2 = ({ 
  projectContacts = [],
  isLoading = false, 
  error = null,
  onViewAllClick,
  maxContactsToShow = 3
}) => {
  
  // Intelligent contact extraction function
  const extractContactInfo = (contactData) => {
    if (!contactData) return null;
    
    // Handle nested contact structure
    const contact = contactData.contact || contactData;
    
    // Smart name extraction with multiple fallback patterns
    let name = null;
    let title = null;
    let email = null;
    let phone = null;
    
    // Try various name field patterns
    if (contact.name && contact.name !== 'Client Representative') {
      name = contact.name;
    } else if (contact.full_name && contact.full_name !== 'Client Representative') {
      name = contact.full_name;
    } else if (contact.first_name && contact.last_name) {
      name = `${contact.first_name} ${contact.last_name}`;
    } else if (contact.contact_name && contact.contact_name !== 'Client Representative') {
      name = contact.contact_name;
    }
    
    // Extract title, avoiding "Client Representative"
    if (contact.title && contact.title !== 'Client Representative') {
      title = contact.title;
    } else if (contact.position && contact.position !== 'Client Representative') {
      title = contact.position;
    } else if (contact.role && contact.role !== 'Client Representative') {
      title = contact.role;
    }
    
    // Extract contact methods
    email = contact.email || contact.email_address || null;
    phone = contact.phone || contact.phone_number || contact.telephone || null;
    
    // Final fallback logic - never show "Client Representative"
    const displayName = name || title || (email ? email.split('@')[0] : null) || 'Contact Available';
    
    return {
      name: displayName,
      title: title && title !== displayName ? title : null,
      email: email,
      phone: phone,
      isPrimary: contactData.is_primary || contact.is_primary || false,
      projectCount: contactData.project_count || contact.project_count || 0
    };
  };

  // Handle loading state
  if (isLoading) {
    return (
      <Box sx={{ 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mt: 2,
        p: 2,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100px'
      }}>
        <CircularProgress size={24} sx={{ color: 'text.secondary' }} />
      </Box>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Box sx={{ 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mt: 2,
        p: 2
      }}>
        <Typography variant="subtitle2" sx={{ 
          fontSize: '0.7rem', 
          fontWeight: 'bold', 
          mb: 1,
          letterSpacing: '0.5px'
        }}>
          CLIENT CONTACTS
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {error}
        </Typography>
      </Box>
    );
  }

  // Process and filter contacts
  const processedContacts = projectContacts
    .map(extractContactInfo)
    .filter(contact => contact && contact.name && contact.name !== 'Client Representative')
    .slice(0, maxContactsToShow);

  const hasMoreContacts = projectContacts.length > maxContactsToShow;

  return (
    <Box sx={{ 
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      mt: 2,
      p: 2
    }}>
      <Typography variant="subtitle2" sx={{ 
        fontSize: '0.7rem', 
        fontWeight: 'bold', 
        mb: 1.5,
        letterSpacing: '0.5px'
      }}>
        CLIENT CONTACTS
      </Typography>

      {processedContacts.length === 0 ? (
        <Typography variant="body2" sx={{ 
          fontSize: '0.75rem', 
          color: 'text.secondary',
          fontStyle: 'italic'
        }}>
          No project contacts available
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {processedContacts.map((contact, index) => (
            <Box key={index} sx={{ 
              borderBottom: index < processedContacts.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
              pb: index < processedContacts.length - 1 ? 1.5 : 0
            }}>
              {/* Contact Name and Primary Badge */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <PersonIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 'medium',
                  flexGrow: 1
                }}>
                  {contact.name}
                  {contact.title && ` (${contact.title})`}
                </Typography>
                {contact.isPrimary && (
                  <Chip 
                    label="PRIMARY" 
                    size="small" 
                    sx={{ 
                      fontSize: '0.6rem', 
                      height: '16px',
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText'
                    }} 
                  />
                )}
              </Box>

              {/* Contact Details */}
              <Box sx={{ ml: 2.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {contact.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      {contact.email}
                    </Typography>
                  </Box>
                )}
                
                {contact.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      {contact.phone}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Projects Count */}
              {contact.projectCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, ml: 2.5 }}>
                  <FolderIcon sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                    {contact.projectCount} {contact.projectCount === 1 ? 'Project' : 'Projects'}
                  </Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* View All Button */}
      {(hasMoreContacts || processedContacts.length > 0) && onViewAllClick && (
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
          <Button
            size="small"
            onClick={onViewAllClick}
            sx={{ 
              fontSize: '0.65rem',
              textTransform: 'none',
              color: 'primary.main'
            }}
          >
            {hasMoreContacts 
              ? `View All ${projectContacts.length} Contacts` 
              : 'View Contact Details'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

ClientContactSectionV2.propTypes = {
  projectContacts: PropTypes.arrayOf(PropTypes.shape({
    contact: PropTypes.shape({
      name: PropTypes.string,
      full_name: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      title: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string
    }),
    is_primary: PropTypes.bool,
    projects: PropTypes.arrayOf(PropTypes.string),
    project_names: PropTypes.arrayOf(PropTypes.string),
    project_count: PropTypes.number
  })),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onViewAllClick: PropTypes.func,
  maxContactsToShow: PropTypes.number
};

export default ClientContactSectionV2;
